import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Phone, Mail, User, MessageSquare } from 'lucide-react';
import { getGeoLocation, submitLead, countryCodes, findCountryByCode } from '../services/api';

export const LeadPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    selectedCountry: countryCodes[0], // default US
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

  // Fetch geolocation on mount
  useEffect(() => {
    getGeoLocation().then(geo => {
      if (geo?.data) {
        setGeoData(geo);
        const matched = findCountryByCode(geo.data.countryCode);
        setFormData(prev => ({ ...prev, selectedCountry: matched }));
      }
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.country-dropdown-container')) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setFormData(prev => ({
          name: '',
          email: '',
          selectedCountry: prev.selectedCountry, // keep geo-detected country
          phone: '',
          message: ''
        }));
        setError('');
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: numericValue }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountrySelect = (country) => {
    setFormData(prev => ({ ...prev, selectedCountry: country }));
    setIsCountryDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name.trim()) { setError('Please enter your name'); return; }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address'); return;
    }
    if (!formData.phone || formData.phone.length < 7) { setError('Please enter a valid phone number'); return; }

    setIsSubmitting(true);
    try {
      const result = await submitLead({
        name: formData.name.trim(),
        email: formData.email.trim(),
        countryDialCode: formData.selectedCountry.dialCode,
        country: formData.selectedCountry.country,
        phoneNumber: formData.phone,
        description: formData.message.trim(),
        city: geoData?.data?.cityName || '',
        state: geoData?.data?.regionName || '',
        ipAddress: geoData?.data?.ipAddress || '',
        geoLocation: geoData ? JSON.stringify(geoData) : ''
      });

      if (result.success) {
        onClose();
        navigate(`/application/main?customerid=${result.customerId}`);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="lead-popup">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div 
          className="relative w-full max-w-md bg-white shadow-2xl animate-fade-in-up"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="lead-popup-close"
          >
            <X size={18} />
          </button>

          {/* Header */}
          <div className="bg-[#0A2A5E] px-6 py-5">
            <h2 className="text-xl font-bold text-white font-['Manrope']">Get Legal Help</h2>
            <p className="text-gray-300 text-xs mt-1">Complete the form and we'll reach out within 24 hours.</p>
          </div>

          {/* Content */}
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-3">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-3 py-2 text-xs" data-testid="lead-error">
                  {error}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={15} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 
                               focus:outline-none focus:border-[#0A2A5E] focus:bg-white transition-all"
                    data-testid="lead-name-input"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail size={15} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 
                               focus:outline-none focus:border-[#0A2A5E] focus:bg-white transition-all"
                    data-testid="lead-email-input"
                  />
                </div>
              </div>

              {/* Country Display */}
              {geoData?.data?.countryName && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                  <div className="px-3 py-2.5 text-sm bg-gray-100 border border-gray-200 text-gray-600" data-testid="lead-country-display">
                    {geoData.data.countryName}
                  </div>
                </div>
              )}

              {/* Phone */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative country-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                      className="flex items-center gap-1.5 px-2.5 py-2.5 bg-gray-50 border border-gray-200 text-gray-700 
                                 hover:bg-gray-100 focus:outline-none focus:border-[#0A2A5E] transition-all text-sm min-w-[80px]"
                      data-testid="lead-country-select"
                    >
                      <span className="text-xs font-medium">+{formData.selectedCountry.dialCode}</span>
                      <svg className={`w-3 h-3 text-gray-400 transition-transform ${isCountryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {isCountryDropdownOpen && (
                      <div className="absolute bottom-full left-0 mb-1 w-44 bg-white border border-gray-200 shadow-lg z-50 max-h-48 overflow-y-auto">
                        {countryCodes.map((c, index) => (
                          <button
                            key={`${c.country}-${index}`}
                            type="button"
                            onClick={() => handleCountrySelect(c)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <span>{c.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone size={15} className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone number"
                      inputMode="numeric"
                      className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 
                                 focus:outline-none focus:border-[#0A2A5E] focus:bg-white transition-all"
                      data-testid="lead-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  How Can We Help? <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute top-2.5 left-0 pl-3 pointer-events-none">
                    <MessageSquare size={15} className="text-gray-400" />
                  </div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Briefly describe your legal matter..."
                    rows={2}
                    className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 
                               focus:outline-none focus:border-[#0A2A5E] focus:bg-white transition-all resize-none"
                    data-testid="lead-message-input"
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0A2A5E] text-white py-3 font-semibold uppercase tracking-wider text-xs
                           hover:bg-[#1a4a8e] disabled:opacity-60 disabled:cursor-not-allowed
                           transition-all flex items-center justify-center gap-2"
                data-testid="lead-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit Request</span>
                )}
              </button>

              <p className="text-[10px] text-gray-500 text-center">
                By submitting, you agree to our <a href="/privacy" className="text-[#0A2A5E] hover:underline">Privacy Policy</a>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
